"use client";

import { useEffect, useState } from "react";

export type DesktopFolderEntry = { name: string; path: string };
export type DesktopBrowseResult = {
  path: string;
  parent: string | null;
  folders: DesktopFolderEntry[];
};
export type DesktopTestPathResult = {
  ok: boolean;
  message: string;
  latencyMs: number;
};
export type DesktopDeviceInfo = {
  deviceId: string;
  hostname: string;
  os: string;
  appVersion: string;
};
export type DesktopTranscribePayload = {
  audio: ArrayBuffer;
  mimeType: string;
  language?: string;
};

type DesktopApi = {
  browseFolders?: (target: string | null) => Promise<DesktopBrowseResult>;
  testPath?: (target: string) => Promise<DesktopTestPathResult>;
  getDeviceInfo?: () => Promise<DesktopDeviceInfo>;
  setAuthToken?: (token: string) => Promise<void>;
  hasAuthToken?: () => Promise<boolean>;
  transcribeAvailable?: () => Promise<boolean>;
  transcribeAudio?: (payload: DesktopTranscribePayload) => Promise<string>;
  windowMinimize?: () => Promise<void>;
  windowMaximizeToggle?: () => Promise<void>;
  windowClose?: () => Promise<void>;
  windowIsMaximized?: () => Promise<boolean>;
  onWindowState?: (cb: (state: { maximized: boolean }) => void) => () => void;
};

declare global {
  interface Window {
    acc?: DesktopApi;
  }
}

export function getDesktopApi(): DesktopApi | null {
  if (typeof window === "undefined") return null;
  return window.acc ?? null;
}

// SSR-safe: starts false on the server / first client render to avoid hydration
// mismatch, then flips to true on mount if the Electron preload is present.
export function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    setIsDesktop(!!getDesktopApi());
  }, []);
  return isDesktop;
}

/**
 * Resolves the running device's ID and registers it with the cloud so the
 * tenant has a record of it. Returns `null` outside the desktop app or if
 * registration fails — callers should fall back to tenant-wide listings.
 *
 * The registration call itself is idempotent on the server, so re-running
 * this hook on every mount is safe.
 */
export function useDeviceRegistration(): { deviceId: string | null; ready: boolean } {
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const api = getDesktopApi();
      if (!api?.getDeviceInfo) {
        if (!cancelled) setReady(true);
        return;
      }
      try {
        const info = await api.getDeviceInfo();
        const res = await fetch("/api/devices/register", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(info),
        });
        if (cancelled) return;
        // We trust the device-side ID even if the cloud call fails — the
        // server will reject mismatches when the user tries to act on it.
        setDeviceId(info.deviceId);
        if (!res.ok) {
          console.warn("[desktop] device register failed:", res.status);
        } else {
          // First-registration responses include a bearer token. Subsequent
          // calls return token=null because the cloud only issues once. If
          // main has lost its plaintext (e.g. device.json migration), trigger
          // an explicit rotation so the JobRunner can authenticate again.
          let receivedToken: string | null = null;
          try {
            const data = await res.json();
            if (typeof data?.token === "string" && data.token.length >= 32) {
              receivedToken = data.token;
            }
          } catch (parseErr) {
            console.warn("[desktop] register response parse failed:", parseErr);
          }

          if (receivedToken && api.setAuthToken) {
            await api.setAuthToken(receivedToken);
          } else if (api.hasAuthToken && api.setAuthToken) {
            const has = await api.hasAuthToken().catch(() => true);
            if (!has) {
              try {
                const rot = await fetch(
                  `/api/devices/${encodeURIComponent(info.deviceId)}/rotate-token`,
                  { method: "POST" },
                );
                if (rot.ok) {
                  const rdata = await rot.json();
                  if (typeof rdata?.token === "string" && rdata.token.length >= 32) {
                    await api.setAuthToken(rdata.token);
                  }
                } else if (rot.status !== 403) {
                  // 403 = caller lacks settings.edit; nothing to log noisily,
                  // operator-level users don't need the JobRunner anyway.
                  console.warn("[desktop] token rotate failed:", rot.status);
                }
              } catch (rotErr) {
                console.warn("[desktop] token rotate error:", rotErr);
              }
            }
          }
        }
      } catch (err) {
        console.warn("[desktop] device register error:", err);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { deviceId, ready };
}

export const DESKTOP_DOWNLOAD_URL =
  process.env.NEXT_PUBLIC_DESKTOP_DOWNLOAD_URL ?? "https://fixcontrol.ai/download";